import { create } from "zustand";
import { persist } from "zustand/middleware";
import { auth, onAuthStateChanged } from '../../../../auth';
import convex, { waitForConvexAuthReady } from "../../../../convexClient";
import { api } from "../../../../convex/_generated/api";
import { SHORT_CACHE_TTL } from "../../../../utils/cacheConfig";
import { withActiveTenant } from "../../../../utils/tenantUtils";

const CACHE_EXPIRATION_TIME = SHORT_CACHE_TTL;

let authReadyPromise = null;
function ensureUser() {
  if (auth.currentUser) return Promise.resolve(auth.currentUser);
  if (!authReadyPromise) {
    authReadyPromise = new Promise((resolve) => {
      const unsub = onAuthStateChanged(auth, (u) => { unsub(); resolve(u || null); });
    });
  }
  return authReadyPromise;
}

const useGoalStore = create(
  persist(
    (set, get) => ({
      goals: [],
      loading: false,
      error: null,
      lastFetched: null,
      filter: "Active",

      setGoals: (goals) => set({ goals, lastFetched: Date.now() }),
      setLoading: (loading) => set({ loading }),
      setError: (error) => set({ error }),
      clearError: () => set({ error: null }),

      fetchGoals: async (includePayments = true, { force = false } = {}) => {
        if (get().loading) return;
        const lastFetched = get().lastFetched;
        if (!force && lastFetched && Date.now() - lastFetched < CACHE_EXPIRATION_TIME) return;

        const user = await ensureUser();
        if (!user) { set({ goals: [], loading: false }); return; }

        set({ loading: true, error: null });
        try {
          await waitForConvexAuthReady();
          const goals = await convex.query(api.goals.listGoals, withActiveTenant());

          if (!includePayments) {
            set({ goals });
            return;
          }

          // Fetch transactions to compute cumulative payments per goal
          const allTxs = await convex.query(api.transactions.listByUser, withActiveTenant());
          const byGoal = {};
          allTxs.forEach((tx) => {
            const goalId = tx.goalId || tx.goal;
            if (!goalId) return;
            const amt = parseFloat(tx.amount || 0) || 0;
            byGoal[goalId] = (byGoal[goalId] || 0) + amt;
          });

          const withPayments = goals.map((goal) => ({
            ...goal,
            id: goal._id || goal.id,
            cumulativePayments: byGoal[goal._id || goal.id] || 0,
          }));

          set({ goals: withPayments });
        } catch (error) {
          set({ error: error?.message || "Failed to fetch goals" });
        } finally {
          set({ loading: false });
        }
      },

      updateGoalStatus: async (goalId, newStatus) => {
        const user = auth.currentUser;
        if (!user) { set({ error: "User not authenticated" }); return; }
        try {
          await convex.mutation(api.goals.updateGoalStatus, { id: goalId, status: newStatus });
          set({ goals: get().goals.map((g) => (g.id === goalId ? { ...g, status: newStatus } : g)) });
        } catch (error) {
          set({ error: error.message });
        }
      },

      deleteGoal: async (goalId) => {
        const user = auth.currentUser;
        if (!user) { set({ error: "User not authenticated" }); return; }
        try {
          await convex.mutation(api.goals.deleteGoal, { id: goalId });
          set({ goals: get().goals.filter((g) => g.id !== goalId) });
        } catch (error) {
          set({ error: error.message });
        }
      },

      setFilter: (filter) => set({ filter }),
    }),
    {
      name: "goal-store",
      partialize: (state) => ({
        goals: state.goals,
        lastFetched: state.lastFetched,
        filter: state.filter,
      }),
    }
  )
);

export default useGoalStore;
